import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import confirm from './ConfirmModal';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { DecryptedContentVersionDto } from '../hooks/useThread';
import type { WebSearchSource } from '../services/aiRequestTypes';

interface MessageComponentProps {
  message?: DecryptedContentVersionDto; // Optional for streaming messages
  onDelete?: (messageId: number) => void;
  currentUserId?: number; // Current logged-in user ID for comparison
  // Streaming AI response props
  isStreaming?: boolean;
  streamingContent?: string;
  streamingThinking?: string;
  streamingWebSearchSources?: WebSearchSource[];
}

const MessageComponent: React.FC<MessageComponentProps> = ({ 
  message, 
  onDelete, 
  currentUserId,
  isStreaming, 
  streamingContent, 
  streamingThinking,
  streamingWebSearchSources
}) => {
  const [showThinking, setShowThinking] = useState(isStreaming ?? false);
  const [copiedCodeBlocks, setCopiedCodeBlocks] = useState<Set<string>>(new Set());
  const [showRaw, setShowRaw] = useState(false);

  const toggleThinking = useCallback(() => {
    setShowThinking(prev => !prev);
  }, []);

  const toggleRaw = useCallback(() => {
    setShowRaw(prev => !prev);
  }, []);

  const showCopySuccess = useCallback((codeId: string) => {
    setCopiedCodeBlocks(prev => new Set([...prev, codeId]));
    
    setTimeout(() => {
      setCopiedCodeBlocks(prev => {
        const newCopiedSet = new Set(prev);
        newCopiedSet.delete(codeId);
        return newCopiedSet;
      });
    }, 1000);
  }, []);

  const copyToClipboard = useCallback(async (text: string, codeId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showCopySuccess(codeId);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showCopySuccess(codeId);
    }
  }, [showCopySuccess]);

  const handleDelete = useCallback(async () => {
    if (!message || !onDelete) return;
    
    const confirmed = await confirm({
      title: 'Delete Message',
      message: 'Are you sure you want to delete this message? This action cannot be undone.',
      confirmText: 'Delete',
      confirmColor: 'danger',
      cancelText: 'Cancel'
    });
    
    if (confirmed && message.id) {
      onDelete(message.id);
    }
  }, [message, onDelete]);

  const renderMarkdownContent = useCallback((content: string) => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          code: ({ node, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : 'text';
            const codeString = String(children).replace(/\n$/, '');
            const codeId = `code-${Math.random().toString(36).substr(2, 9)}`;
            const isCopied = copiedCodeBlocks.has(codeId);
            
            return language ? (
              <div className="position-relative code-block-container">
                <button
                  className="btn btn-sm btn-outline-secondary position-absolute code-copy-btn"
                  onClick={() => copyToClipboard(codeString, codeId)}
                  title="Copy code"
                  style={{
                    top: '8px',
                    right: '8px',
                    zIndex: 10,
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    opacity: isCopied ? 1 : 0.7,
                    transition: 'opacity 0.2s'
                  }}
                >
                  {isCopied ? '✓' : '⧉'}
                </button>
                <SyntaxHighlighter
                  style={tomorrow as any}
                  language={language}
                  PreTag="div"
                >
                  {codeString}
                </SyntaxHighlighter>
              </div>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          a: ({ node, href, children, ...props }) => {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  if (href) {
                    window.open(href, '_blank', 'noopener,noreferrer');
                  }
                }}
                {...props}
              >
                {children}
              </a>
            );
          }
        }}
      >
        {content}
      </ReactMarkdown>
    );
  }, [copiedCodeBlocks, copyToClipboard]);

  // Determine if this is a streaming message or regular message
  const isUserMessage = message?.messageType === 'user';
  const isAssistantMessage = !isUserMessage;
  const isCurrentUser = message?.createdById === currentUserId;
  
  // Get content and thinking from either message or streaming props
  const content = isStreaming ? (streamingContent || '') : (message?.decryptedData || '');
  const thinking = isStreaming ? streamingThinking : undefined; // DecryptedContentVersionDto doesn't have thinking field
  const timestamp = message ? new Date(message.submittedAt || message.startedAt || new Date()) : new Date();
  
  return (
    <div 
      className={`mb-3 px-3 py-1 rounded position-relative message-hover ${
        isAssistantMessage 
          ? 'bg-body-secondary me-auto' 
          : isCurrentUser 
            ? 'bg-primary text-white ms-auto'
            : 'bg-secondary text-white me-auto'
      }`}
      style={{ maxWidth: 'calc(100% - 40px)', width: 'fit-content' }}
    >
      <div className="mb-1 d-flex justify-content-between align-items-start">
        <div>
          <strong>
            {isUserMessage 
              ? (message?.createdById === currentUserId ? 'You' : (message?.createdByName || 'Unknown User'))
              : 'AI Assistant'
            }
          </strong>
          {isStreaming && (
            <span className="ms-2 badge bg-info">responding...</span>
          )}
        </div>
        
        {/* Message options menu */}
        <UncontrolledDropdown className="position-absolute" style={{ top: '4px', right: '4px' }}>
          <DropdownToggle
            tag="button"
            className="btn btn-sm"
            style={{ fontSize: '1.2rem', lineHeight: 1 }}
          >
            ⋯
          </DropdownToggle>
          <DropdownMenu end>
            {isAssistantMessage && thinking && (
              <DropdownItem onClick={toggleThinking}>
                {showThinking ? 'Hide' : 'Show'} Thinking
              </DropdownItem>
            )}
            {isAssistantMessage && !isStreaming && (
              <DropdownItem onClick={toggleRaw}>
                {showRaw ? 'Show Markdown' : 'Show Raw'}
              </DropdownItem>
            )}
            {!isStreaming && message && (
              <DropdownItem onClick={handleDelete} className="text-danger">
                Delete Message
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
      
      {/* Show thinking section if available and visible */}
      {isAssistantMessage && thinking && showThinking && (
        <div className="mb-3 p-2 bg-secondary bg-opacity-10 rounded">
          <div className="small text-muted mb-1">
            <i className="fas fa-brain me-1"></i>Thinking...
          </div>
          <div style={{ fontSize: '0.9em', fontStyle: 'italic' }}>
            {renderMarkdownContent(thinking)}
            {isStreaming && <span className="text-secondary">▎</span>}
          </div>
        </div>
      )}
      
      <div className={isUserMessage ? '' : 'markdown-content'}>
        {isUserMessage ? (
          <div style={{ whiteSpace: 'pre-wrap' }}>{content}</div>
        ) : (
          <>
            {content ? (
              <>
                {showRaw ? (
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{content}</pre>
                ) : (
                  renderMarkdownContent(content)
                )}
                {isStreaming && <span className="text-primary">▎</span>}
              </>
            ) : (
              thinking && !content ? '...' : ''
            )}
          </>
        )}
      </div>
      
      {/* Web search sources display */}
      {(() => {
        // Use streaming sources if available, otherwise use saved sources
        const sources = isStreaming 
          ? streamingWebSearchSources 
          : message?.decryptedAttributes?.webSearchSources;
        
        if (!sources || sources.length === 0) return null;
        
        // Remove duplicates by URL
        const uniqueSources = sources.filter((source, index, arr) => 
          arr.findIndex(s => s.url === source.url) === index
        );
        
        return (
          <div className="mt-2 pt-2 border-top border-secondary border-opacity-25">
            <div className="small text-muted mb-2">
              <i className="fas fa-search me-1"></i>
              Sources ({uniqueSources.length})
            </div>
            <div className="d-flex flex-wrap gap-1">
              {uniqueSources.map((source, index) => (
                <a
                  key={index}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-secondary btn-sm text-decoration-none"
                  style={{ fontSize: '0.75rem' }}
                  title={source.title || source.url}
                >
                  <i className="fas fa-external-link-alt me-1"></i>
                  {new URL(source.url).hostname}
                </a>
              ))}
            </div>
          </div>
        );
      })()}
      
      <div className="text-end mt-1">
        <small className={
          isAssistantMessage 
            ? 'text-body-secondary' 
            : 'text-white-50'
        }>
          {timestamp.toLocaleTimeString()}
        </small>
      </div>
      
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(MessageComponent, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when props haven't meaningfully changed
  if (prevProps.isStreaming !== nextProps.isStreaming) return false;
  if (prevProps.streamingContent !== nextProps.streamingContent) return false;
  if (prevProps.streamingThinking !== nextProps.streamingThinking) return false;
  if (prevProps.onDelete !== nextProps.onDelete) return false;
  
  // For regular messages, compare the message content
  if (prevProps.message?.id !== nextProps.message?.id) return false;
  if (prevProps.message?.decryptedData !== nextProps.message?.decryptedData) return false;
  if (prevProps.message?.submittedAt !== nextProps.message?.submittedAt) return false;
  if (prevProps.message?.startedAt !== nextProps.message?.startedAt) return false;
  
  return true; // Props are the same, skip re-render
});